import type { ParsedCommandPaletteDirectedQuery } from 'types/commandPaletteDirectedSearch';
import { COMMAND_PALETTE_DIRECTED_MIN_SEARCH_LENGTH } from 'types/commandPaletteDirectedSearch';
import { isBackendCompatibleEmail } from 'utils/emailValidation';

const DIRECTED_PREFIXES = ['GL:', 'cust:', 'supp:'] as const;

function parsePrefixedQuery(query: string, prefix: string): string | null {
  if (!query.toLowerCase().startsWith(prefix.toLowerCase())) {
    return null;
  }

  const searchText = query.slice(prefix.length).trim();
  if (searchText.length < COMMAND_PALETTE_DIRECTED_MIN_SEARCH_LENGTH) {
    return null;
  }

  return searchText;
}

export function parseCommandPaletteDirectedQuery(
  query: string,
): ParsedCommandPaletteDirectedQuery | null {
  const trimmed = query.trim();
  if (!trimmed) {
    return null;
  }

  const generalLedger = parsePrefixedQuery(trimmed, 'GL:');
  if (generalLedger) {
    return { mode: 'general_ledger', searchText: generalLedger };
  }

  const customer = parsePrefixedQuery(trimmed, 'cust:');
  if (customer) {
    return { mode: 'customer', searchText: customer };
  }

  const supplier = parsePrefixedQuery(trimmed, 'supp:');
  if (supplier) {
    return { mode: 'supplier', searchText: supplier };
  }

  if (isBackendCompatibleEmail(trimmed)) {
    return { mode: 'email', email: trimmed.toLowerCase() };
  }

  return null;
}

export function getDirectedSearchAwaitingHint(
  query: string,
): (typeof DIRECTED_PREFIXES)[number] | null {
  const trimmed = query.trim();
  if (!trimmed || isBackendCompatibleEmail(trimmed)) {
    return null;
  }

  for (const prefix of DIRECTED_PREFIXES) {
    if (!trimmed.toLowerCase().startsWith(prefix.toLowerCase())) {
      continue;
    }

    const searchText = trimmed.slice(prefix.length).trim();
    if (searchText.length < COMMAND_PALETTE_DIRECTED_MIN_SEARCH_LENGTH) {
      return prefix;
    }
  }

  return null;
}
